import { useEffect, useId, useRef, useState } from "react";
import { OTP_LENGTH } from "@kovernow/flows/auth";

/**
 * OtpInput (UX contract): one numeric field with `autocomplete=one-time-code`
 * so iOS/Android/password managers fill it, pastes the whole code (spaces and
 * dashes are stripped — no maxLength, so "123 456" isn't truncated), submits
 * itself on the sixth digit and says so in its label. A code completed while
 * busy/blocked submits as soon as that clears. `resetKey` changing (wrong
 * code) clears it and keeps focus in it.
 */
export function OtpInput({
  label,
  error,
  onComplete,
  resetKey,
  busy,
  blocked,
}: {
  label: string;
  error?: string;
  onComplete: (code: string) => void;
  resetKey: number;
  busy?: boolean;
  /** e.g. offline: hold the submit until it clears. */
  blocked?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const valueRef = useRef(value);
  valueRef.current = value;
  /** The code already submitted (or the stale code at a reset). */
  const submitted = useRef<string | null>(null);

  useEffect(() => {
    // Block the stale value from re-submitting before the clear lands.
    submitted.current = valueRef.current;
    setValue("");
    ref.current?.focus();
  }, [resetKey]);

  useEffect(() => {
    if (value.length === OTP_LENGTH && !busy && !blocked && submitted.current !== value) {
      submitted.current = value;
      onComplete(value);
    }
  }, [value, busy, blocked, onComplete]);

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        ref={ref}
        id={id}
        name="otp"
        value={value}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-busy={busy || undefined}
        className="min-h-12 w-full max-w-xs rounded-md border border-input bg-background px-3 text-center font-mono text-2xl tracking-[0.5em]"
        onChange={(e) => {
          // A user edit re-arms submission (even the same code typed again).
          submitted.current = null;
          setValue(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH));
        }}
      />
      {error ? (
        <p id={`${id}-error`} className="text-sm font-semibold text-status-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
